import { Component, effect, OnDestroy, OnInit, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { EnumValues, DynamicFilterConfig, DynamicFilterValues, FilterOption, createDynamicFilterState } from '@card-software/library';
import { IMEXAdminGroup, IMEXEditorGroup, IMEXViewerGroup } from '../../../models/roles/roles-group';
import { Shipment, ShipmentSearch } from '../../../models/shipment/shipment';
import { EnumService } from '../../../services/enum.service';
import { ShipmentProxyService } from '../../../proxies/order/shipment.proxy.service';
import { DeliveryNoteProxyService } from '../../../proxies/order/delivery-note.proxy.service';
import { AuthService } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-shipments',
  templateUrl: './shipments.component.html',
  styleUrl: './shipments.component.scss',
})
export class ShipmentsComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private enumService = inject(EnumService);
  private translate = inject(TranslateService);
  private shipmentProxyService = inject(ShipmentProxyService);
  private deliveryNoteProxyService = inject(DeliveryNoteProxyService);

  isLoading = signal(0);
  listShipments = signal<Shipment[]>([]);
  statuses = signal<EnumValues>({});
  enumsReady = signal(false);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'shipment-filters', config: this.filterConfig });
  showFilters = signal(false);
  currentPage = signal<number>(1);
  totalRecords = signal<number>(0);
  pageSize = signal<number>(25);
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);
  deliveryNoteNumbers = signal<Map<string, string>>(new Map());

  invoiceOptions = computed<FilterOption[]>(() => {
    const invoices = this.listShipments()
      .flatMap((shipment) => shipment.invoices || [])
      .filter(
        (invoice, index, self) => invoice?.invoiceNumber && self.findIndex((i) => i?.invoiceNumber === invoice.invoiceNumber) === index,
      );

    return invoices.map((invoice) => ({
      value: invoice.id || '',
      label: invoice.invoiceNumber || '',
    }));
  });

  canEdit = computed(() => {
    if (
      AuthService.hasRole(IMEXAdminGroup.ShipmentAdmin) ||
      AuthService.hasRole(IMEXAdminGroup.ShipmentSupervisor) ||
      AuthService.hasRole(IMEXEditorGroup.ShipmentEditor)
    ) {
      return true;
    }
    if (AuthService.hasRole(IMEXViewerGroup.ShipmentViewer)) {
      return false;
    }
    return false;
  });

  private destroy$ = new Subject<void>();
  private filtersPanelInitialized = false;
  private lastSearchCriteria: ShipmentSearch = {};

  getRowData(rowData: unknown): Shipment {
    return rowData as Shipment;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('ShipmentStatus', 'orderEnums').values;
        if (Object.keys(statusesValue).length > 0) {
          this.statuses.set(statusesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translate.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translate.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translate.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'trackingNumber',
          label: translate('Shipments_Tracking_Number', 'Tracking Number'),
          placeholder: translate('Enter_tracking_number', 'Enter tracking number'),
          defaultVisible: true,
        },
        /* {
          type: 'date',
          formControlName: 'actualArrivalDate',
          label: translate('Shipments_Actual_Arrival_Date', 'Actual Arrival Date'),
          placeholder: translate('Select_arrival_date', 'Select arrival date'),
          showClear: true,
          defaultVisible: false,
        }, */
        {
          type: 'multiselect',
          formControlName: 'invoiceReferences',
          label: translate('Shipments_Invoice_References', 'Invoice References'),
          placeholder: translate('Select_invoice_references', 'Select invoice references'),
          options: this.invoiceOptions(),
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private onSearch(searchCriteria: ShipmentSearch): void {
    this.startLoading();
    this.lastSearchCriteria = searchCriteria;
    this.shipmentProxyService
      .searchShipment(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder(), false)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const pagedResult = response.data;
          const shipments = pagedResult?.items ?? [];
          this.listShipments.set(shipments);
          this.totalRecords.set(pagedResult?.totalCount ?? 0);
          this.loadDeliveryNoteNumbers(shipments);
        },
        error: (e) => {
          console.error(e);
          this.listShipments.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  private loadDeliveryNoteNumbers(shipments: Shipment[]): void {
    // Collect all unique delivery note IDs
    const allDeliveryNoteIds = new Set<string>();
    shipments.forEach((shipment) => {
      shipment.deliveryNoteReferences?.forEach((id) => allDeliveryNoteIds.add(id));
    });

    if (allDeliveryNoteIds.size === 0) {
      this.deliveryNoteNumbers.set(new Map());
      return;
    }

    // Load all delivery notes
    const dnMap = new Map<string, string>();
    let loadedCount = 0;
    const totalCount = allDeliveryNoteIds.size;

    allDeliveryNoteIds.forEach((dnId) => {
      this.deliveryNoteProxyService.getDeliveryNote(dnId).subscribe({
        next: (response) => {
          if (response.data?.number) {
            dnMap.set(dnId, response.data.number);
          }
          loadedCount++;
          if (loadedCount === totalCount) {
            this.deliveryNoteNumbers.set(dnMap);
          }
        },
        error: (error) => {
          console.error(`Error loading delivery note ${dnId}:`, error);
          loadedCount++;
          if (loadedCount === totalCount) {
            this.deliveryNoteNumbers.set(dnMap);
          }
        },
      });
    });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = this.normalizeSortField(event.field, this.sortField());
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const searchCriteria: ShipmentSearch = {
      trackingNumber: (filters['trackingNumber'] as string)?.trim() || undefined,
      actualArrivalDate: (filters['actualArrivalDate'] as Date | undefined) || undefined,
      invoiceReferences:
        Array.isArray(filters['invoiceReferences']) && (filters['invoiceReferences'] as unknown[]).length > 0
          ? (filters['invoiceReferences'] as unknown[]).map((item: unknown) =>
              typeof item === 'object' && item !== null ? (item as Record<string, string>)['value'] : (item as string),
            )
          : undefined,
    };

    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  public redirectToDetails(param?: string): void {
    const queryParams = {
      id: param ? param : 'new',
    };
    this.router.navigate(['shipment-detail'], { queryParams });
  }

  getInvoices(shipment: Shipment): string {
    return shipment.invoices?.map((invoice) => invoice.invoiceNumber).join(', ') || '';
  }
  getDeliveryNotes(shipment: Shipment): string {
    const deliveryNoteIds = shipment.deliveryNoteReferences;

    if (!deliveryNoteIds || deliveryNoteIds.length === 0) {
      return '';
    }

    const dnMap = this.deliveryNoteNumbers();
    const numbers = deliveryNoteIds.map((id) => dnMap.get(id)).filter((num) => num !== undefined);

    return numbers.join(', ');
  }

  private normalizeSortField(sortField: string | string[] | undefined | null, fallback: string | undefined): string | undefined {
    if (Array.isArray(sortField)) {
      return sortField[0] ?? fallback;
    }
    return sortField ?? fallback;
  }
}
